import React from 'react'
import { useTranslation } from 'react-i18next'
import { MdEdit } from 'react-icons/md'
import { FaArrowRight, FaPlus } from 'react-icons/fa'
import { formatKeyLabel } from './helper'
import { convertDateTimeFullFormatToLocal } from '../../../../utils/dateFormat/dateFormat'
import { EVENT_TYPE } from '../../userManagementConstants'

interface AppInfo {
  appName?: string
  appId?: string
  roleName?: string
}

type InfoValue = string | AppInfo[] | undefined | null

interface ActivityInfo {
  [key: string]: InfoValue
}

export interface RoleActivityItem {
  applicationName?: string
  roleNameFrom?: string
  roleNameTo?: string
}

export interface TemporaryRoleActivityItem extends RoleActivityItem {
  startTimeFrom?: string
  endTimeFrom?: string
  statusFrom?: string
  startTimeTo?: string
  endTimeTo?: string
  statusTo?: string
  applicationNameFrom?: string
  applicationNameTo?: string
}

interface EventDataItem {
  label?: string
  value?: string
  type?: string
}

export interface ActivityLogItem {
  eventKey?: number
  eventGroupKey?: string
  actionType: number
  userName?: string
  applicationName?: string
  actorRole?: string
  keys?: string[]
  timeStampKeys?: string[]
  previousInfo?: ActivityInfo
  newInfo?: ActivityInfo
  roles?: RoleActivityItem[]
  temporaryRoles?: TemporaryRoleActivityItem[]
  eventData?: EventDataItem[]
  heading?: string
  eventTimeStamp?: string
}

interface TimeLineItemProps {
  item: ActivityLogItem
  isUserActivity?: boolean
}

const TimeLineItem: React.FC<TimeLineItemProps> = ({
  item,
  isUserActivity = false,
}) => {
  const { t } = useTranslation(['translation'])

  const getDotColorAndIcon = () => {
    switch (item?.actionType) {
      case EVENT_TYPE.STATUS_CHANGE:
        return {
          dotColor: 'bg-primary',
          dotIcon: <FaArrowRight color="#fff" size={10} />,
        }
      case EVENT_TYPE.DATA_CHANGE:
        return {
          dotColor: 'bg-primary',
          dotIcon: <MdEdit color="#fff" size={10} />,
        }
      case EVENT_TYPE.DATA_ADDITION:
        return {
          dotColor: 'bg-success',
          dotIcon: <FaPlus color="#fff" size={10} />,
        }
    }
    return {
      dotColor: 'bg-success',
      dotIcon: <MdEdit color="#fff" size={10} />,
    }
  }
  const { dotColor, dotIcon } = getDotColorAndIcon()

  // const dotIcon =
  //   item?.heading === 'Added user' ? (
  //     <FaPlus color="#fff" size={10} />
  //   ) : (
  //     <MdEdit color="#fff" size={10} />
  //   )

  console.log('Rendering TimeLineItem with item:', item) // Debug log to inspect the item structure

  const getUserDeatils = (): string => {
    const userName = item?.userName
    const role = item?.actorRole
    const appName = item?.applicationName

    console.log('User Activity Item:', item) // Debug log to inspect the item structure
    let userDetails = ''
    userDetails = appName && appName?.length > 0 ? userDetails + appName : ''
    if (role && role?.length > 0) {
      userDetails = userDetails?.length > 0 ? `${userDetails} : ${role}` : role
    }
    return userDetails?.length > 0
      ? `${userName}(${userDetails})`
      : userName ?? ''
  }

  const toCamelCase = (key: string): string =>
    key.charAt(0).toLowerCase() + key.slice(1)

  const getLabelValueForStatusChangeEvent = (isOld = false): string => {
    const keysArray = item?.keys ?? []
    let information = ''
    const details = isOld ? item?.previousInfo : item?.newInfo
    keysArray?.forEach((key) => {
      const value =
        (details?.[toCamelCase(key)] as string) ??
        t('userRoles.activity_log_none')
      information =
        information?.length > 0 ? `${information} : ${value}` : `${value}`
    })
    return information
  }

  const getFormattedLabelValue = (
    key: string,
    isOldValue: boolean,
  ): string | undefined => {
    const timeStampKeys = item?.timeStampKeys ?? []
    const information = isOldValue ? item?.previousInfo : item?.newInfo
    const val = information?.[toCamelCase(key)]
    if (Array.isArray(val)) {
      if (val.length === 0) return t('userRoles.activity_log_none')
      return val
        .map(
          (a: AppInfo) =>
            `${a?.appName ?? a?.appId ?? ''}(${a?.roleName ?? ''})`,
        )
        .join(', ')
    }
    const camelKey = toCamelCase(key)
    if (timeStampKeys?.includes(camelKey)) {
      const rawVal = information?.[camelKey]
      if (typeof rawVal === 'string' && rawVal.length > 0) {
        return convertDateTimeFullFormatToLocal(rawVal)
      } else {
        return undefined
      }
    } else {
      return information?.[camelKey] as string | undefined
    }
  }

  const formatTransition = (
    from?: string,
    to?: string,
    isDateTime = false,
  ): string => {
    const formatValue = (value?: string): string => {
      if (!value) return t('userRoles.activity_log_none')
      return isDateTime ? convertDateTimeFullFormatToLocal(value) : value
    }

    return `${formatValue(from)} ${t(
      'userRoles.activity_log_to_arrow',
    )} ${formatValue(to)}`
  }

  const groupedRoles = item?.roles ?? []
  const groupedTemporaryRoles = item?.temporaryRoles ?? []

  const rolesHeadingStyle = {
    backgroundColor: '#e7f1ff',
    color: '#114a6e',
    border: '1px solid #b6d4fe',
    borderRadius: '4px',
    padding: '2px 8px',
    width: 'fit-content',
  }

  return (
    <div className={`position-relative d-flex p-2`}>
      {/* Dot and vertical line */}
      <div
        className="d-flex flex-column align-items-center mt-1"
        style={{ width: '30px' }}
      >
        <div
          className={`rounded-circle d-flex justify-content-center align-items-center ${dotColor}`}
          style={{
            width: '20px',
            height: '20px',
            zIndex: 2,
            backgroundClip: 'padding-box',
          }}
        >
          {dotIcon}
        </div>

        <div
          className="bg-light mt-2"
          style={{
            width: 2,
            flexGrow: 1,
            zIndex: 1,
            opacity: 0.5,
          }}
        />
      </div>

      {/* Content block */}
      <div className="ms-3 flex-grow-1 w-100">
        <div className="d-flex flex-column w-100 gap-2">
          <div className="d-flex flex-column">
            <div className="">
              <strong className="me-1">{getUserDeatils()}</strong>
              <span>{item?.heading}</span>
            </div>

            <small className="text-muted">
              {convertDateTimeFullFormatToLocal(item?.eventTimeStamp ?? '')}
            </small>
          </div>

          <div className="d-flex gap-3">
            {item?.actionType === EVENT_TYPE.STATUS_CHANGE ? (
              <div className="d-flex gap-1 align-items-center">
                <span>{getLabelValueForStatusChangeEvent(true)}</span>
                <strong>
                  {<FaArrowRight size={15} className="text-primary" />}
                </strong>
                <strong>{getLabelValueForStatusChangeEvent(false)}</strong>
              </div>
            ) : (
              <div className="d-flex flex-column">
                {item?.keys?.map((key) => (
                  <div key={key} className="d-flex gap-1 align-items-center">
                    <strong>{formatKeyLabel(key)}:</strong>
                    <span>
                      {getFormattedLabelValue(key, true) ||
                        t('userRoles.activity_log_none')}
                    </span>
                    <span>
                      {<FaArrowRight size={15} className="text-primary" />}
                    </span>
                    <span>
                      {getFormattedLabelValue(key, false) ||
                        t('userRoles.activity_log_none')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {isUserActivity && (item?.roles?.length ?? 0) > 0 && (
            <div className="d-flex flex-column gap-2">
              <strong style={rolesHeadingStyle}>
                {t('userRoles.activity_log_roles')}
              </strong>
              {groupedRoles?.map((role, index) => (
                <div
                  key={`${index}-${role.applicationName}`}
                  className="d-flex flex-column"
                >
                  <strong>{role?.applicationName}</strong>
                  <div>
                    {formatTransition(role?.roleNameFrom, role?.roleNameTo)}
                  </div>
                </div>
              ))}
            </div>
          )}
          {isUserActivity && (item?.temporaryRoles?.length ?? 0) > 0 && (
            <div className="d-flex flex-column gap-2">
              <strong style={rolesHeadingStyle}>
                {t('userRoles.activity_log_temporary_roles')}
              </strong>
              {groupedTemporaryRoles?.map((temporaryRoleItem, index) => (
                <div
                  key={`temporary_roles_${index}`}
                  className="d-flex flex-column"
                >
                  <div>
                    <strong>{`${t(
                      'users.activity_log_application',
                    )}: `}</strong>
                    {formatTransition(
                      temporaryRoleItem?.applicationNameFrom,
                      temporaryRoleItem?.applicationNameTo,
                    )}
                  </div>
                  <div>
                    <strong>{`${t('userRoles.activity_log_role')}: `}</strong>
                    {formatTransition(
                      temporaryRoleItem?.roleNameFrom,
                      temporaryRoleItem?.roleNameTo,
                    )}
                  </div>
                  <div>
                    <strong>{`${t(
                      'userRoles.activity_log_start_time',
                    )}: `}</strong>
                    {formatTransition(
                      temporaryRoleItem?.startTimeFrom,
                      temporaryRoleItem?.startTimeTo,
                      true,
                    )}
                  </div>
                  <div>
                    <strong>{`${t(
                      'userRoles.activity_log_end_time',
                    )}: `}</strong>
                    {formatTransition(
                      temporaryRoleItem?.endTimeFrom,
                      temporaryRoleItem?.endTimeTo,
                      true,
                    )}
                  </div>
                  <div>
                    <strong>{`${t('userRoles.activity_log_status')}: `}</strong>
                    {formatTransition(
                      temporaryRoleItem?.statusFrom,
                      temporaryRoleItem?.statusTo,
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TimeLineItem
